/**
 * Assessment end / resume — Firestore update builders (one-clinician SLaM).
 *
 * At the end of the Clinical Interview a one-clinician SLaM Lead may decide
 * to END the assessment instead of proceeding to the Semi-Structured
 * Interview (see CIEndDecisionDialog). That decision touches three places on
 * the patient document, written together in one transaction so the overview,
 * the dashboard summary and the report pipeline can never see a half-state:
 *
 *   1. workflowDecisions.ciEndDecision = 'endAssessment'
 *      The runtime flag every reader keys off (useWorkflowConfig
 *      assessmentEndedAfterCI, backend assessment_ended_after_ci).
 *   2. assessmentEnd = { reason, nextSteps, endedAt, endedBy, endedByName, resumed* }
 *      A ROOT-level record (not inside dataModel). Root placement is
 *      deliberate: Firestore rules let Support and unassigned Org Admins
 *      update root fields but not formData/dataModel, so they can "Resume
 *      assessment" without a Cloud Function or a rules change. The reason
 *      (required) and the recommended next steps (optional, '' when none)
 *      are what the Assessment Conclusion paragraph (ci_END_summary) is
 *      drafted from. Both are clinical free text and never leave Firestore
 *      for summaries or analytics.
 *   3. CI completion + ciReport* generated fields, exactly as the normal CI
 *      finish writes them, plus ciReportApproved reset to false because a new
 *      paragraph now needs review.
 *
 * "Resume assessment" (AssessmentEndedBanner) deletes the decision key and
 * stamps resumedAt / resumedBy / resumedByName on the same record so the
 * history of the end is kept. Nothing else needs cleaning up: CI report
 * approval is derived from the review page's docStructure ∩ outputs, so the
 * Assessment Conclusion row simply disappears from the review once the
 * decision is gone.
 *
 * The helpers are pure apart from the Firestore sentinels they embed
 * (serverTimestamp / deleteField), so tests mock `firebase/firestore`.
 */

import { deleteField, serverTimestamp } from 'firebase/firestore';
import { CI_END_DECISIONS } from '../constants/workflowConfig';
import {
  buildFirstCompletedFieldsDotNotation,
  buildFirstGeneratedFieldsDotNotation,
} from './reportAuditHelpers';

/** Report section title used to generate the Assessment Conclusion paragraph. */
export const ASSESSMENT_CONCLUSION_SECTION_TITLE = 'Assessment Conclusion';

/** reportAuditHistory event types written by the end / resume actions. */
export const ASSESSMENT_END_AUDIT_EVENTS = Object.freeze({
  ENDED: 'assessmentEndedAfterCi',
  RESUMED: 'assessmentResumedAfterCi',
});

/**
 * Build the single-transaction update map for "End assessment" at the end of
 * the Clinical Interview.
 *
 * @param {Object} args
 * @param {Object|null} args.patientDataModel  Full patient document, used for
 *   the write-once first-completion / first-generation stamps.
 * @param {{uid: string, displayName?: string}} args.user  Signed-in clinician.
 * @param {string} args.reason  Trimmed, non-empty reason typed by the clinician.
 * @param {string} [args.nextSteps]  Recommended next steps typed by the
 *   clinician. Optional; stored as '' when blank or omitted so the record
 *   always carries a string and readers never see undefined.
 * @returns {{ updates: Object, didWriteFirstCompleted: boolean, isFirstGeneration: boolean }}
 */
export function buildEndAssessmentUpdates({ patientDataModel, user, reason, nextSteps }) {
  const trimmedReason = typeof reason === 'string' ? reason.trim() : '';
  if (!trimmedReason) {
    throw new Error('buildEndAssessmentUpdates: a reason is required');
  }
  const trimmedNextSteps = typeof nextSteps === 'string' ? nextSteps.trim() : '';

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
  const ciStage = patientDataModel?.dataModel?.['2_clinicalInterview'];
  const firstCompletedFields = buildFirstCompletedFieldsDotNotation('2_clinicalInterview', ciStage);
  const isFirstGeneration = !manageReports?.ciReportFirstGeneratedAt;

  const updates = {
    'workflowDecisions.ciEndDecision': CI_END_DECISIONS.END_ASSESSMENT,
    // Whole-object write so a previous end/resume cycle's resumed* fields are
    // cleared rather than left dangling next to the new endedAt.
    assessmentEnd: {
      reason: trimmedReason,
      nextSteps: trimmedNextSteps,
      endedAt: serverTimestamp(),
      endedBy: user?.uid ?? null,
      endedByName: user?.displayName ?? null,
      resumedAt: null,
      resumedBy: null,
      resumedByName: null,
    },
    'dataModel.2_clinicalInterview.status': 'completed',
    'dataModel.2_clinicalInterview.progress': 100,
    'dataModel.2_clinicalInterview.docs_available': true,
    'dataModel.2_clinicalInterview.lastCompletedAt': serverTimestamp(),
    'dataModel.2_clinicalInterview.lastCompletedBy': user?.uid ?? null,
    'dataModel.2_clinicalInterview.lastCompletedByName': user?.displayName ?? null,
    ...firstCompletedFields,
    'dataModel.6_manageReports.ciReportGenerated': true,
    'dataModel.6_manageReports.ciReportGeneratedAt': serverTimestamp(),
    'dataModel.6_manageReports.ciReportGeneratedBy': user?.uid ?? null,
    'dataModel.6_manageReports.ciReportGeneratedByName': user?.displayName ?? null,
    ...buildFirstGeneratedFieldsDotNotation('ci', manageReports),
    // A new paragraph (Assessment Conclusion) is about to be drafted, so the
    // CI report must be reviewed again even if it had been approved before.
    'dataModel.6_manageReports.ciReportApproved': false,
  };

  return {
    updates,
    didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
    isFirstGeneration,
  };
}

/**
 * Build the update map for "Proceed to Semi-Structured Interview" — records
 * the decision only; the CI completion itself is written by the page's normal
 * completion handler.
 */
export function buildProceedToSsiUpdates() {
  return {
    'workflowDecisions.ciEndDecision': CI_END_DECISIONS.PROCEED_TO_SSI,
  };
}

/**
 * Build the update map for "Resume assessment" from the ended state. Deletes
 * the decision (so the SSI card unlocks and the end-of-CI question is asked
 * again if the CI is re-finished) and stamps who resumed and when.
 *
 * @param {{uid: string, displayName?: string}} user  Signed-in user (Lead,
 *   Org Admin or Support).
 */
export function buildResumeAssessmentUpdates({ user }) {
  return {
    'workflowDecisions.ciEndDecision': deleteField(),
    'assessmentEnd.resumedAt': serverTimestamp(),
    'assessmentEnd.resumedBy': user?.uid ?? null,
    'assessmentEnd.resumedByName': user?.displayName ?? null,
  };
}
